import {booleanAttribute, DestroyRef, ElementRef, HostBinding, Injectable, Input, OnDestroy, OnInit, signal} from "@angular/core";
import {AbstractControl, ControlValueAccessor, FormControl, NgControl, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {MatFormField, MatFormFieldControl} from "@angular/material/form-field";
import {MatAutocomplete} from "@angular/material/autocomplete";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Subject, tap} from "rxjs";
import {map} from "rxjs/operators";
import {MatOption} from "@angular/material/core";
import {BooleanInput, coerceBooleanProperty} from "@angular/cdk/coercion";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {Temporal} from "temporal-polyfill";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import PlainTime = Temporal.PlainTime;

const debug = initDebugger('BasisKlokkeslettVarighetKomponent');

/** Basisklasse for komponenter som operer på klokkeslett.
 * Merk at vi ikke gjøre noen tidssone-justeringer, verdien bruker angir propageres uendret til
 * angitt NgControl/ControlValueAccessor som en {@link PlainTime} instans.
 * */
@Injectable() //nødvendig for at ikke ts kompilator skal klage med 'Class is using Angular features but is not decorated ..'
export abstract class BasisKlokkeslettVelgerKomponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<PlainTime> {

    //'Named capturing group' er ikke støttet før i ES2018 - men alle de vanligste nettleserne har støttet dette lenge så anser det som ufarlig å bruker her
    // @ts-ignore
    static klokkeslettRegexp = /^\s*(?<timer>([01]?\d)|(2[0-3])):(?<minutter>[0-5]\d)\s*$/;

    /** skjemakontroller for tekstfelt bruker kan skrive klokkeslett inn i */
    klokkeslettTekstfelt = new FormControl('', {
        nonNullable: true,
        validators: [Validators.pattern(BasisKlokkeslettVelgerKomponent.klokkeslettRegexp)]
    })

    /** Aktiv tidssone, for indikasjon av tidssone i GUI */
    tidssone: string;

    protected klokkeslettSomPlainTime = signal<PlainTime | null>(null);

    protected constructor(/** skjemakontrollen tilknyttet denne komponenten via formControl eller ngModel direktiver*/
                          private tilknyttetSkjemakontroll: NgControl,
                          /** mulig MatFormField som omslutter denne komponenten */
                          private matFormField: MatFormField | null,
                          private destroyRef: DestroyRef,
                          /** aktiv tidssone, for indikasjon av tidssone i GUI  */
                          gjeldendeTidssone: GjeldendeTidssone,
                          private logprefiks: string) {
        // setter valueAccessor direkte fremfor via DI og providers i @Component med `{provide: NG_VALUE_ACCESSOR, multi:true, useExisting: KlokkeslettVelgerKomponent}`
        // for å unngå sirkulær avhengighet.
        this.tilknyttetSkjemakontroll.valueAccessor = this;
        this.tidssone = gjeldendeTidssone.brukersTidssone().somZoneId();
        this.lyttTilKlokkeslettTekstfeltEndringer();
    }

    vedFokusMottatt() {
        if (erIkkeNil(this.vedFeltBerørt)) this.vedFeltBerørt();

        //MatFormFieldControl relatert
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
        }
    }

    vedFokusMistet() {
        if (this.vedFeltBerørt) this.vedFeltBerørt(); //@todo ok å fjrne?

        //MatFormFieldControl relatert
        this.focused = false;
        if (erIkkeNil(this.vedFeltBerørt)) this.vedFeltBerørt();
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    vedNedtrekksmenyAapnet() {
        this.debug("vedNedtrekksmenyAapnet")
        //må sørge for at denne koden kjører etter at mat-autocomplete kode har kjørt
        setTimeout(() =>
          this.fokuserNærmesteAlternativINedtrekksliste(this.klokkeslettTekstfelt.value));
    }

    /** Formaterer klokkeslett for bruk i inputfelt: hh:mm.
     * Må holdes i synk med {@lin #strengTilPlainTime} */
    plainTimeTilStreng(klokkeslett: PlainTime | null): string {
        //@todo bør vi støtte ulike format her?
        return klokkeslett?.toLocaleString('nb', {timeStyle: 'short', timeZone: 'UTC'}) ?? ''
    }

    /** Sann dersom komponenten er benyttet inne i en mat-form-field */
    erMatFormField(): boolean {
        return erIkkeNil(this.matFormField);
    }

    //forventes imlp av subklasse

    /** MAT autocomplete kontrolleren, ikke satt før ngAfterViewInit.
     * Deklareres med '@ViewChild(MatAutocomplete)' i subklasse, av ukjent grunn fungerer det ikke å definere feltet i superklassen */
    abstract get autocomplete(): MatAutocomplete | null;

    /** Referanse til tekstfelt, ikke satt før ngAfterViewInit.
     * Deklareres med '@ViewChild('tekstfelt', {static:false})' i subklasse, av ukjent grunn fungerer det ikke å definere feltet i superklassen */
    abstract get tekstfelt(): ElementRef<HTMLInputElement> | null;

    /** Finner matchende MatOption innslag fra options-listen til mat-autocomplete komponenten.
     * null dersom intet treff.
     * public for testbarhet */
    abstract finnNærmesteValgAlternativ(klokkeslett: string): MatOption | null;

    // hjelpemetoder

    /** Leser hh:mm formatert streng og returnerer en PlainTime representasjon av dette klokkeslettet.
     * Må holdes i synk med {@lin #plainTimeTilStreng}*/
    protected strengTilPlainTime(klokkeslett: string): PlainTime | null {
        const gyldigKlokkeslett = klokkeslett.match(BasisKlokkeslettVelgerKomponent.klokkeslettRegexp);
        if (erNil(gyldigKlokkeslett)) return null;

        return new PlainTime(Number.parseInt(gyldigKlokkeslett.groups!.timer), Number.parseInt(gyldigKlokkeslett.groups!.minutter));
    }

    /** Sørger for at tilknyttet skjemakontroll kjører validering, som i sin tur vil kalle {@link #tilknyttetSkjemakontrollValidator} */
    protected propagerValideringsfeilTilTilknyttetSkjemakontroll() {
        this.tilknyttetSkjemakontroll.control?.updateValueAndValidity();
    }

    protected eksponerEndretKlokkeslett(klokkeslett: PlainTime | null) {
        if (erIkkeNil(this.vedFeltEndret))
            this.vedFeltEndret(klokkeslett);
    }

    private lyttTilKlokkeslettTekstfeltEndringer() {
        this.klokkeslettTekstfelt.valueChanges
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(muligKlokkeslett => this.fokuserNærmesteAlternativINedtrekksliste(muligKlokkeslett)),
            tap(muligKlokkeslett => this.propagerValideringsfeilTilTilknyttetSkjemakontroll()),
            //filter(muligKlokkeslett => this.klokkeslettTekstfelt.valid), //ikke eksponer ny verdi dersom valideringsfeil
            map(muligKlokkeslett => this.strengTilPlainTime(muligKlokkeslett)))
          .subscribe(klokkeslett => {
              this.debug("eksponerer klokkeslett", klokkeslett?.toString(), this.klokkeslettTekstfelt.dirty, this.klokkeslettTekstfelt.touched)
              this.klokkeslettSomPlainTime.set(klokkeslett);
              //signaliser at skjemaverdien har blitt endret, men kun dersom endringen er utført av bruker via GUI-feltet
              if (this.klokkeslettTekstfelt.dirty) this.eksponerEndretKlokkeslett(klokkeslett);
          });
    }

    private fokuserNærmesteAlternativINedtrekksliste(verdi: string) {
        this.debug("fokuserNærmesteAlternativINedtrekksliste", verdi)
        if (erNil(this.autocomplete) || erNil(this.autocomplete.options)) return;
        const valgAlternativ = this.finnNærmesteValgAlternativ(verdi)
        if (erNil(valgAlternativ)) return;

        this.debug("fokuserNærmesteAlternativINedtrekksliste valgAlternativ", valgAlternativ.viewValue)
        //scroll til det aktuelle elementet i nedtrekkslisten, og marker dette som aktivt
        valgAlternativ._getHostElement().scrollIntoView({block: 'center'})
        this.autocomplete._keyManager.setActiveItem(valgAlternativ);
    }

    protected debug(melding: string, ...argumenter:any[]) {
        debug(`(${this.logprefiks}) ${melding}`, ...argumenter);
    }

    //ng2 livssykelmetoder

    ngOnInit(): void {
        //sørger for at valideringsfeil knyttet til klokkeslettTekstfelt propagerer til skjemakontroll tilknyttet denne komponenten
        //kan ikke gjøres i konstruktør da tilknyttetSkjemakontroll.control ikke er satt enda
        this.tilknyttetSkjemakontroll.control!.addValidators(this.tilknyttetSkjemakontrollValidator())
    }

    /** Returnerer valideringsfeil registrert på {@link #klokkeslettTekstfelt}.
     * Se også {@link #propagerValideringsfeilTilTilknyttetSkjemakontroll} */
    private tilknyttetSkjemakontrollValidator(): ValidatorFn {
        return (control: AbstractControl<PlainTime>): ValidationErrors | null => {
            //les valideringsfeil fra feltKontroll og returner disse
            this.debug("tilknyttetSkjemakontrollValidator", this.klokkeslettTekstfelt.errors)
            return this.klokkeslettTekstfelt.errors;
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    //ControlValueAccessor implementasjon

    /** Settes via {@link #registerOnChange} (ifbm ControlValueAccessor)*/
    private vedFeltEndret: Function | null = null;

    /** Settes via {@link #registerOnTouched} (ifbm ControlValueAccessor)*/
    private vedFeltBerørt: Function | null = null;

    registerOnChange(vedEndring: Function): void {
        //this.debug("registerOnChange", vedEndring)
        this.vedFeltEndret = vedEndring;
    }

    registerOnTouched(vedBerørt: Function): void {
        //this.debug("registerOnTouched", vedBerørt)
        this.vedFeltBerørt = vedBerørt;
    }

    setDisabledState(isDisabled: boolean): void {
        this.debug("setDisabledState", isDisabled)
        if (isDisabled) this.klokkeslettTekstfelt.disable();
        else this.klokkeslettTekstfelt.enable();
    }

    writeValue(klokkeslett: PlainTime | null): void {
        this.debug("writeValue", klokkeslett?.toString())
        this.klokkeslettTekstfelt.setValue(this.plainTimeTilStreng(klokkeslett));
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    //MatFormFieldControl implementasjon

    /** brukes til å lage unike IDer, se {@link #id}*/
    static nesteId = 0;

    private _placeholder: string = 'hh:mm';
    private _required = false;

    @Input('aria-describedby') userAriaDescribedBy!: string; //@todo hva gjør vi med denne?

    get placeholder() {
        return this.shouldLabelFloat || !this.erMatFormField() ? this._placeholder : '';
    }

    @Input() set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get required() {
        return this._required ||
          (this.tilknyttetSkjemakontroll.control?.hasValidator(Validators.required) ?? false);
    }

    @Input({transform: booleanAttribute}) set required(req: boolean) {
        this._required = req;
        if (req) this.klokkeslettTekstfelt.addValidators(Validators.required);
        else this.klokkeslettTekstfelt.removeValidators(Validators.required);
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get disabled(): boolean {
        return this.klokkeslettTekstfelt.disabled
    }

    @Input() set disabled(value: BooleanInput) {
        this.setDisabledState(coerceBooleanProperty(value))
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    public get ngControl(): NgControl {
        return this.tilknyttetSkjemakontroll;
    }

    readonly stateChanges = new Subject<void>();

    @HostBinding() id = `varighet-velger-${BasisKlokkeslettVelgerKomponent.nesteId++}`;

    controlType = 'klokkeslett-velger';

    /** se {@link #vedFokusMottatt} / {@link #vedFokusMistet} */
    focused = false;

    get value(): PlainTime | null {
        return this.klokkeslettSomPlainTime();
    }

    set value(klokkeslett: PlainTime | null) {
        this.writeValue(klokkeslett);
    }

    get empty() {
        return erTomEllerNil(this.klokkeslettTekstfelt.value)
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    get errorState(): boolean {
        return this.klokkeslettTekstfelt.invalid;
    }

    setDescribedByIds(ids: string[]) {
        if (erNil(this.tekstfelt)) return;
        const controlElement = this.tekstfelt.nativeElement;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() == 'input') return; //trenger ikke å sette fokus dersom klikket var i inputfeltet
        if (erNil(this.tekstfelt)) return;

        this.tekstfelt.nativeElement.focus();
    }
}
